/**
 * UsageChip — compact credit-balance indicator for the Navbar.
 * Shows remaining credits with a progress ring against the plan's monthly
 * allowance (or the 30-credit trial for Free users). Links to /credits.
 */
import { Link } from "react-router-dom";
import { Coins, Sparkles } from "lucide-react";
import { useUsage } from "@/context/UsageContext";

export function UsageChip() {
  const { usage } = useUsage();
  if (!usage) return null;

  const isFree = usage.package_id === "free";
  const balance = usage.credits_balance || 0;
  const allowance = isFree ? (usage.trial_credits || 30) : (usage.credits_per_month || 0);
  const pct = allowance > 0 ? Math.min(100, Math.round((balance / allowance) * 100)) : 100;
  const ring = balance <= 4 ? "text-rose-400" : "text-violet-400";

  return (
    <Link
      to="/credits"
      data-testid="navbar-usage-chip"
      className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 transition-all group"
      title={isFree && !usage.email_verified ? "Verify your email to unlock 30 trial credits" : `${balance} credits available`}
    >
      <span className="relative w-5 h-5 inline-flex shrink-0">
        <svg viewBox="0 0 20 20" className={`w-5 h-5 -rotate-90 ${ring}`} aria-hidden="true">
          <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="2.5" />
          <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeDasharray={`${(pct / 100) * 50.27} 50.27`} />
        </svg>
      </span>
      <span className="inline-flex items-center gap-1 text-xs font-medium text-white/75 group-hover:text-white transition-colors tabular-nums" data-testid="navbar-credits-balance">
        <Coins className="w-3 h-3 text-violet-300" />{balance}<span className="text-white/40">credits</span>
      </span>
      {isFree && (
        <span className="hidden lg:inline-flex items-center gap-1 ml-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-500/20 to-indigo-500/20 text-violet-300 text-[10px] font-bold border border-violet-400/30">
          <Sparkles className="w-3 h-3" /> {usage.email_verified ? "Upgrade" : "Verify"}
        </span>
      )}
    </Link>
  );
}
